import React from 'react';
import { Phone, Wrench, Shield, CheckCircle, Star, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Link } from 'react-router-dom';

import SeoNav from '../components/seo/SeoNav';
import FAQSection from '../components/seo/FAQSection';
import { CtaSection, SeoFooter } from '../components/seo/CtaFooter';

const FAQ = [
  { q: "Mon téléphone est-il réparable après un dégât des eaux ?", r: "Cela dépend de la rapidité d'intervention et de l'étendue des dégâts. Plus vous agissez vite, plus les chances de récupération sont élevées. Nous effectuons un diagnostic gratuit pour évaluer l'état exact." },
  { q: "Combien coûte la réparation d'un téléphone tombé dans l'eau ?", r: "Le prix varie selon l'étendue des dégâts et les composants à remplacer (écran, batterie, carte mère). Le diagnostic est gratuit, nous vous donnons ensuite un devis précis avant toute intervention." },
  { q: "Combien de temps faut-il pour la réparation ?", r: "Selon la gravité, entre quelques heures et 2 jours. Le séchage complet et les tests de sécurité prennent du temps. Nous vous tenons informé tout au long du processus." },
  { q: "Le riz fonctionne-t-il vraiment ?", r: "Non, c'est un mythe ! Le riz absorbe l'eau très lentement et peut même laisser de la poussière dans le téléphone. La seule vraie solution est un démontage et nettoyage professionnel rapide." },
  { q: "Mes données seront-elles récupérées ?", r: "Dans la plupart des cas, oui. Nous faisons tout notre possible pour préserver vos données. Si la carte mère n'est pas trop endommagée, vos photos, contacts et données restent intacts." },
  { q: "La garantie couvre-t-elle les dégâts des eaux ?", r: "Notre garantie de 3 mois couvre le travail effectué et les pièces remplacées. Cependant, nous ne pouvons garantir qu'aucun nouveau problème n'apparaîtra suite à une oxydation résiduelle non détectable immédiatement." },
];

const ReparationDegatsEauxSaintChamond = () => {
  return (
    <div className="min-h-screen bg-white">
      <SeoNav />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#1a2332] via-[#1e2a3d] to-[#1a2332] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500 rounded-full px-6 py-2 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400" />
              <span className="font-semibold text-red-400">INTERVENTION D'URGENCE</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Réparation Dégâts des Eaux Saint-Chamond
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Téléphone tombé dans l'eau ? Intervention rapide contre l'oxydation - Chaque minute compte !
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <a href="tel:0773601521" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-red-500 hover:bg-red-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl animate-pulse">
                <Phone className="w-5 h-5 mr-2" />
                Appeler MAINTENANT
              </Button>
            </a>
            <Link to="/rendez-vous" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                Prendre rendez-vous
              </Button>
            </Link>
            <a href="https://wa.me/33773601521" target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#25D366] hover:bg-[#20BD5A] text-white px-10 py-6 text-lg rounded-xl transition-all font-bold shadow-xl">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                WhatsApp
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <AlertTriangle className="w-8 h-8 text-red-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">URGENT</div>
              <div className="text-sm text-gray-300">Action rapide</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Shield className="w-8 h-8 text-green-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">3 mois</div>
              <div className="text-sm text-gray-300">Garantie</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Wrench className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">Expert</div>
              <div className="text-sm text-gray-300">Oxydation</div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-purple-400 mx-auto mb-2" />
              <div className="text-2xl font-bold">+1000</div>
              <div className="text-sm text-gray-300">Clients satisfaits</div>
            </div>
          </div>
        </div>
      </section>

      {/* Alerte urgence */}
      <section className="py-8 px-4 bg-red-50 border-y-4 border-red-500">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-12 h-12 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-bold text-red-900 mb-2">
                ⚠️ ATTENTION : Chaque minute compte !
              </h3>
              <p className="text-red-800 text-lg mb-3">
                Téléphone tombé dans l'eau ? <strong>N'ALLUMEZ PAS votre téléphone</strong> et contactez-nous immédiatement.
              </p>
              <ul className="space-y-2 text-red-900">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span><strong>Éteignez</strong> immédiatement le téléphone s'il est allumé</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span><strong>Retirez</strong> la coque, la carte SIM et la carte SD</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span><strong>Séchez</strong> délicatement l'extérieur avec un chiffon</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span><strong>Ne mettez PAS</strong> au four, micro-ondes ou sèche-cheveux</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                  <span><strong>Contactez-nous</strong> au 07.73.60.15.21 dans l'heure</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Types de dégâts */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nous traitons tous les dégâts des eaux
            </h2>
            <p className="text-lg text-gray-600">Quel que soit le liquide, nous pouvons intervenir</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Eau douce</h3>
                <p className="text-gray-600">
                  Tombé dans l'évier, la baignoire, une flaque d'eau ou les toilettes (eau propre).
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Piscine / Mer</h3>
                <p className="text-gray-600">
                  Contact avec l'eau chlorée de piscine ou l'eau salée de mer (plus corrosive).
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Boissons sucrées</h3>
                <p className="text-gray-600">
                  Soda, jus de fruit, café, thé ou autres liquides sucrés qui collent et oxydent.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Pluie</h3>
                <p className="text-gray-600">
                  Exposition à la pluie ou humidité prolongée qui s'infiltre dans le téléphone.
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Condensation</h3>
                <p className="text-gray-600">
                  Humidité accumulée à l'intérieur du téléphone (changement de température).
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-xl transition-shadow">
              <CardContent className="p-6">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-yellow-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Autres liquides</h3>
                <p className="text-gray-600">
                  Bière, vin, savon, parfum ou tout autre liquide renversé accidentellement.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Notre processus */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Notre processus de traitement
            </h2>
            <p className="text-lg text-gray-600">Intervention professionnelle contre l'oxydation</p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card className="bg-white shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-600">1</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Démontage complet</h3>
                <p className="text-gray-600">
                  Ouverture et démontage complet pour accéder à tous les composants.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-green-600">2</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Nettoyage ultrasons</h3>
                <p className="text-gray-600">
                  Nettoyage professionnel aux ultrasons pour éliminer l'oxydation.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-purple-600">3</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Séchage complet</h3>
                <p className="text-gray-600">
                  Séchage en profondeur de tous les composants électroniques.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg">
              <CardContent className="p-6 text-center">
                <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-yellow-600">4</span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Tests et remontage</h3>
                <p className="text-gray-600">
                  Vérification complète de toutes les fonctionnalités et remontage.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <FAQSection titre="Questions fréquentes" items={FAQ} sousTitre="Tout savoir sur la réparation après dégâts des eaux" bg="bg-white" />

      {/* CTA Final */}
      <CtaSection urgence titre="⚠️ Téléphone tombé dans l'eau ? Agissez MAINTENANT !" texte="Chaque minute compte. Contactez-nous immédiatement pour une intervention d'urgence à Saint-Chamond." />

      {/* Footer */}
      <SeoFooter texte="© 2026 Suro Phone - Réparation Dégâts des Eaux Saint-Chamond" />
    </div>
  );
};

export default ReparationDegatsEauxSaintChamond;
